import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material';
import biometricsService, { PersonBiometricsRow } from '../services/biometricsService';

type Shot = { file: File; previewUrl: string };

type GuideStepId = 'front' | 'left' | 'right';
type GuideState = {
  step: GuideStepId;
  message: string;
  ok: boolean;
  debug?: string;
};

const MIRROR_PREVIEW = true;

const BiometricsPage: React.FC = () => {
  const [rows, setRows] = React.useState<PersonBiometricsRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [employeeNo, setEmployeeNo] = React.useState('');
  const [companyName, setCompanyName] = React.useState('');
  const [isExternal, setIsExternal] = React.useState(false);
  const [isActive, setIsActive] = React.useState(true);
  const [consent, setConsent] = React.useState(false);

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const [cameraOn, setCameraOn] = React.useState(false);
  const [shots, setShots] = React.useState<Shot[]>([]);
  const [personUuid, setPersonUuid] = React.useState<string | null>(null);

  // FaceID-like: komendy + auto-ujęcia
  const [autoCapture, setAutoCapture] = React.useState(true);
  const [guide, setGuide] = React.useState<GuideState>({
    step: 'front',
    message: 'Włącz kamerę, aby rozpocząć.',
    ok: false,
  });
  const stableOkCountRef = React.useRef(0);
  const lastShotAtRef = React.useRef(0);
  const leftRightSignRef = React.useRef<number | null>(null); // kalibracja znaku yaw dla "lewo"

  const landmarkerRef = React.useRef<any>(null);
  const mpReadyRef = React.useRef(false);
  const mpBusyRef = React.useRef(false);

  const refresh = React.useCallback(async () => {
    try {
      const list = await biometricsService.list(50);
      setRows(list);
    } catch (e: any) {
      setError(e?.message || 'Nie udało się pobrać listy');
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  React.useEffect(() => {
    return () => {
      // cleanup stream + object urls
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      } catch {}
      setShots((prev) => {
        for (const s of prev) {
          try { URL.revokeObjectURL(s.previewUrl); } catch {}
        }
        return [];
      });
    };
  }, []);

  const startCamera = async () => {
    setError(null);
    setSuccess(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);

      // Lazy-init MediaPipe (web) do prowadzenia jak FaceID
      if (!landmarkerRef.current) {
        try {
          const vision = await import('@mediapipe/tasks-vision');
          const FilesetResolver = (vision as any).FilesetResolver;
          const FaceLandmarker = (vision as any).FaceLandmarker;
          const fileset = await FilesetResolver.forVisionTasks(
            'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.22-rc.20250304/wasm'
          );
          landmarkerRef.current = await FaceLandmarker.createFromOptions(fileset, {
            baseOptions: {
              modelAssetPath:
                'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
            },
            runningMode: 'VIDEO',
            numFaces: 1,
            outputFaceBlendshapes: false,
            outputFacialTransformationMatrixes: false,
          });
          mpReadyRef.current = true;
        } catch (e: any) {
          mpReadyRef.current = false;
          setError(e?.message || 'Nie udało się załadować MediaPipe (prowadzenie FaceID).');
        }
      } else {
        mpReadyRef.current = true;
      }
    } catch (e: any) {
      setCameraOn(false);
      setError(e?.message || 'Nie udało się uruchomić kamery. Sprawdź uprawnienia w przeglądarce.');
    }
  };

  const stopCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } catch {}
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch {}
      // @ts-ignore
      videoRef.current.srcObject = null;
    }
    setCameraOn(false);
    mpReadyRef.current = false;
    stableOkCountRef.current = 0;
    lastShotAtRef.current = 0;
  };

  const takeShot = async () => {
    setError(null);
    setSuccess(null);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);

    const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
    });
    if (!blob) {
      setError('Nie udało się wykonać ujęcia (blob=null).');
      return;
    }

    const uuid = personUuid || (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : String(Date.now()));
    if (!personUuid) setPersonUuid(uuid);

    const file = new File([blob], `face_${uuid}_${shots.length + 1}.jpg`, { type: 'image/jpeg' });
    const previewUrl = URL.createObjectURL(blob);
    setShots((prev) => [...prev, { file, previewUrl }]);
  };

  const getYawMetric = (landmarks: Array<{ x: number; y: number }>) => {
    // Metryka skrętu głowy (lewo/prawo) z symetrii: policzek lewy/prawy i czubek nosa.
    // Indexy z face mesh (MediaPipe): nose tip ~ 1, left cheek ~ 234, right cheek ~ 454.
    const nose = landmarks[1];
    const leftCheek = landmarks[234];
    const rightCheek = landmarks[454];
    if (!nose || !leftCheek || !rightCheek) return null;
    const dxL = Math.abs(nose.x - leftCheek.x);
    const dxR = Math.abs(rightCheek.x - nose.x);
    const denom = dxL + dxR;
    if (!denom) return null;
    return (dxR - dxL) / denom; // ~[-1..1]
  };

  const bboxFromLandmarks = (landmarks: Array<{ x: number; y: number }>) => {
    let minX = 1, minY = 1, maxX = 0, maxY = 0;
    for (const p of landmarks) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const w = maxX - minX;
    const h = maxY - minY;
    return { minX, minY, maxX, maxY, cx, cy, w, h };
  };

  const isInsideOval = (cx: number, cy: number) => {
    // dopasowane do overlay: height ~82%, aspect 3/4 => width ~61.5%
    const rx = 0.615 / 2;
    const ry = 0.82 / 2;
    const dx = (cx - 0.5) / rx;
    const dy = (cy - 0.5) / ry;
    return (dx * dx + dy * dy) <= 1.0;
  };

  const computeGuide = React.useCallback((landmarks: any[] | null): GuideState => {
    if (!cameraOn) return { step: guide.step, message: 'Włącz kamerę, aby rozpocząć.', ok: false };
    if (!landmarks || landmarks.length < 10) return { step: guide.step, message: 'Nie widzę twarzy. Ustaw się w owalu.', ok: false, debug: 'no_face' };

    const box = bboxFromLandmarks(landmarks as any);
    const inside = isInsideOval(box.cx, box.cy);
    const faceH = box.h;

    if (!inside) return { step: guide.step, message: 'Wycentruj twarz w owalu.', ok: false, debug: 'not_centered' };
    if (faceH < 0.28) return { step: guide.step, message: 'Podejdź bliżej (twarz za mała).', ok: false, debug: `faceH=${faceH.toFixed(2)}` };
    if (faceH > 0.70) return { step: guide.step, message: 'Oddal się trochę (twarz za duża).', ok: false, debug: `faceH=${faceH.toFixed(2)}` };

    const yaw = getYawMetric(landmarks as any);
    if (yaw === null) return { step: guide.step, message: 'Ustaw twarz w owalu.', ok: false };

    const step = guide.step;
    const absYaw = Math.abs(yaw);
    const frontOk = absYaw < 0.08;

    if (step === 'front') {
      return { step, message: frontOk ? 'OK. Trzymaj nieruchomo…' : 'Patrz na wprost (prosto w kamerę).', ok: frontOk, debug: `yaw=${yaw.toFixed(3)}` };
    }

    if (step === 'left') {
      const targetOk = absYaw > 0.18 && absYaw < 0.55;
      const ok = targetOk && (leftRightSignRef.current ? Math.sign(yaw) === leftRightSignRef.current : true);
      return { step, message: targetOk ? 'OK. Trzymaj nieruchomo…' : 'Obróć głowę w lewo (profil).', ok, debug: `yaw=${yaw.toFixed(3)}` };
    }

    // right
    const targetOk = absYaw > 0.18 && absYaw < 0.55;
    const ok = targetOk && (leftRightSignRef.current ? Math.sign(yaw) === -leftRightSignRef.current : true);
    return { step, message: targetOk ? 'OK. Trzymaj nieruchomo…' : 'Obróć głowę w prawo (profil).', ok, debug: `yaw=${yaw.toFixed(3)}` };
  }, [cameraOn, guide.step]);

  React.useEffect(() => {
    if (!cameraOn || !autoCapture) return;
    if (!mpReadyRef.current || !videoRef.current || !landmarkerRef.current) {
      setGuide((g) => ({ ...g, message: 'Ładuję detekcję twarzy…', ok: false }));
      return;
    }

    let cancelled = false;
    const interval = window.setInterval(async () => {
      if (cancelled) return;
      if (mpBusyRef.current) return;
      mpBusyRef.current = true;
      try {
        const video = videoRef.current;
        if (!video) return;
        const now = performance.now();
        const res = landmarkerRef.current.detectForVideo(video, now);
        const faceLandmarks = res?.faceLandmarks?.[0] || null;

        const next = computeGuide(faceLandmarks);
        setGuide(next);

        const stableNeeded = 5; // ~750ms
        const cooldownMs = 900;
        if (next.ok) stableOkCountRef.current += 1;
        else stableOkCountRef.current = 0;

        const nowTs = Date.now();
        const canShoot = (nowTs - lastShotAtRef.current) > cooldownMs;
        if (next.ok && stableOkCountRef.current >= stableNeeded && canShoot) {
          // Kalibracja znaku "lewo": zapisujemy przy pierwszym profilu
          if (guide.step === 'left' && leftRightSignRef.current === null && faceLandmarks) {
            const yaw = getYawMetric(faceLandmarks as any);
            if (yaw !== null && Math.abs(yaw) > 0.12) {
              leftRightSignRef.current = Math.sign(yaw) || 1;
            }
          }

          await takeShot();
          lastShotAtRef.current = nowTs;
          stableOkCountRef.current = 0;

          setGuide((g) => {
            if (g.step === 'front') return { step: 'left', message: 'Obróć głowę w lewo (profil).', ok: false };
            if (g.step === 'left') return { step: 'right', message: 'Obróć głowę w prawo (profil).', ok: false };
            return { step: 'right', message: 'Zebrano ujęcia. Możesz dodać więcej lub zapisać.', ok: false };
          });
        }
      } catch (e: any) {
        setError(e?.message || 'Błąd prowadzenia (MediaPipe).');
      } finally {
        mpBusyRef.current = false;
      }
    }, 150);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [cameraOn, autoCapture, computeGuide, guide.step, takeShot]);

  const clearShots = () => {
    setShots((prev) => {
      for (const s of prev) {
        try { URL.revokeObjectURL(s.previewUrl); } catch {}
      }
      return [];
    });
    setPersonUuid(null);
    stableOkCountRef.current = 0;
    lastShotAtRef.current = 0;
    leftRightSignRef.current = null;
    setGuide({
      step: 'front',
      message: cameraOn ? 'Ustaw twarz w owalu i patrz na wprost.' : 'Włącz kamerę, aby rozpocząć.',
      ok: false,
    });
  };

  const canSubmit =
    firstName.trim() &&
    lastName.trim() &&
    companyName.trim() &&
    shots.length > 0 &&
    consent;

  const onSubmit = async () => {
    if (shots.length === 0) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const uuid = personUuid || (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : String(Date.now()));
      if (!personUuid) setPersonUuid(uuid);

      const res = await biometricsService.enroll({
        person_uuid: uuid,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        employee_no: employeeNo.trim() || undefined,
        company_name: companyName.trim(),
        is_external_company: isExternal,
        is_active: isActive,
        consent_obtained: consent,
        images: shots.map((s) => s.file),
        metadata: { source: 'web-camera' },
      });
      setSuccess(`Dodano osobę i zapisano ${res.created_ids?.length || 0} embedding(ów).`);
      setFirstName('');
      setLastName('');
      setEmployeeNo('');
      setCompanyName('');
      setIsExternal(false);
      setIsActive(true);
      setConsent(false);
      clearShots();
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Nie udało się dodać danych');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography
          component="h1"
          sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '20px', lineHeight: '24px', fontWeight: 400, color: '#020242', m: 0, mt: '8.5px' }}
        >
          Biometrics – dodawanie twarzy
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Imię"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nazwisko"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nr ewidencyjny (opcjonalnie)"
              value={employeeNo}
              onChange={(e) => setEmployeeNo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Firma"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={isExternal} onChange={(e) => setIsExternal(e.target.checked)} />}
              label="Firma zewnętrzna"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label="Aktywny"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
              label="Mam zgodę na przetwarzanie danych biometrycznych (wymagane)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {!cameraOn ? (
                <Button variant="outlined" onClick={startCamera}>
                  Włącz kamerę
                </Button>
              ) : (
                <>
                  <Button variant="outlined" onClick={takeShot}>
                    Zrób ujęcie
                  </Button>
                  <Button variant="text" onClick={stopCamera}>
                    Wyłącz kamerę
                  </Button>
                </>
              )}
              <Button variant="text" onClick={clearShots} disabled={shots.length === 0}>
                Wyczyść ujęcia
              </Button>
            </Box>
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoCapture}
                    onChange={(e) => {
                      setAutoCapture(e.target.checked);
                      stableOkCountRef.current = 0;
                      lastShotAtRef.current = 0;
                    }}
                  />
                }
                label="Tryb FaceID (auto-komendy + auto-ujęcia)"
              />
              <Typography variant="body2" sx={{ color: guide.ok ? '#16A34A' : 'text.secondary', mt: 0.5 }}>
                {guide.message}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Ujęcia: {shots.length} {personUuid ? `(person_uuid: ${personUuid})` : ''}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ border: '1px solid #E6E6EC', borderRadius: 1, overflow: 'hidden' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '16/9',
                  bgcolor: '#000',
                  transform: MIRROR_PREVIEW ? 'scaleX(-1)' : 'none',
                  transformOrigin: 'center',
                }}
              >
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {/* Owalna ramka (guideline) */}
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      // Pionowy owal (jak FaceID) – niezależny od proporcji 16:9 kontenera
                      height: '82%',
                      aspectRatio: '3 / 4',
                      width: 'auto',
                      borderRadius: '999px',
                      border: '4px solid',
                      borderColor: cameraOn ? '#22C55E' : 'rgba(255,255,255,0.35)',
                      boxShadow: cameraOn ? '0 0 0 9999px rgba(0,0,0,0.35)' : '0 0 0 9999px rgba(0,0,0,0.55)',
                      outline: '1px solid rgba(255,255,255,0.12)',
                    }}
                  />
                </Box>
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    left: 12,
                    right: 12,
                    bottom: 10,
                    pointerEvents: 'none',
                    color: 'rgba(255,255,255,0.92)',
                    fontSize: 12,
                    textShadow: '0 1px 2px rgba(0,0,0,0.75)',
                    display: 'flex',
                    justifyContent: 'center',
                    // kontener jest "mirror", więc odwracamy tekst z powrotem dla czytelności
                    transform: MIRROR_PREVIEW ? 'scaleX(-1)' : 'none',
                  }}
                >
                  {cameraOn ? guide.message : 'Włącz kamerę, aby zrobić ujęcia'}
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {shots.map((s, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: 110,
                    height: 80,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid #E6E6EC',
                    position: 'relative',
                  }}
                >
                  <Box component="img" src={s.previewUrl} alt={`Ujęcie ${idx + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ))}
              {shots.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Zrób kilka ujęć twarzy (np. 5–10), najlepiej z lekkim skrętem głowy.
                </Typography>
              )}
            </Box>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={!canSubmit || loading}
              sx={{ backgroundColor: '#4040DB', '&:hover': { backgroundColor: '#1F1FB0' } }}
            >
              {loading ? 'Dodawanie…' : 'Dodaj i policz embeddingi'}
            </Button>
            {!consent && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Aby zapisać embedding, zaznacz zgodę.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: 500, mb: 1 }}>
          Ostatnio dodane
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Osoba</TableCell>
                <TableCell>Firma</TableCell>
                <TableCell>External</TableCell>
                <TableCell>Dim</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Utworzono</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                    Brak danych
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.first_name} {r.last_name}</TableCell>
                    <TableCell>{r.company_name}</TableCell>
                    <TableCell>{r.is_external_company ? 'tak' : 'nie'}</TableCell>
                    <TableCell>{r.embedding_dim}</TableCell>
                    <TableCell>{r.model_name}</TableCell>
                    <TableCell>{r.created_at}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default BiometricsPage;

